import React, { useState, useEffect } from 'react';
import '../profile.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconLockOpen, IconX, IconMedicalCross } from '@tabler/icons-react';
import MHeader from '@/components/ui/organisms/MHeader';
import { useProfileNavigation } from '@/config/navigation/profile-navigation';
import MLoader from '@/components/ui/atoms/MLoader';
import { getMemberInfo } from '@/hooks/useMembers';
import { MedicalDetailType, MedicalDetailStatus } from '@/types/health-profile';
import MDialog from '@/components/ui/atoms/MDialog';
import { useProfileSummary } from '@/hooks/useHealthProfileSummary';
import { useToast } from '@/components/ui/atoms/MToastProvider';
import MSpacer from '@/components/ui/atoms/MSpacer';
import { useFamilyStats } from '@/hooks/useFamilyStats';
import { useVitals } from '@/hooks/useVitals';
import { useInsurance } from '@/hooks/useInsurance';
import { emergencyInfoService } from '@/services/emergency-info.service';
import { isChild as isChildUtil } from '@/types/member';
import familyStatsService from '@/services/familyStats.service';
import styles from './ActivateEmergencyCard.module.css';
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, ANALYTICS_SCREEN_NAMES } from '@/constants/analytics.constants';

const ActivateEmergencyCard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingNav, setPendingNav] = useState<{
    path: string;
    id: string;
    type: MedicalDetailType;
  } | null>(null);
  const { healthProfileNav, userProfileNav } = useProfileNavigation();
  const memberInfo = getMemberInfo();

  // Fetch the profile summary
  const { summary, updateNone, loading } = useProfileSummary(memberInfo.id);
  
  // Fetch member stats to check emergencyCardReady status
  const { memberStats, fetchMemberStats } = useFamilyStats({ memberInfo });
  
  // Fetch vitals, insurance, and emergency contacts for completion check
  const { vitals, triggerRefetch } = useVitals(memberInfo.id);
  const { insurances, fetchInsurance } = useInsurance({ memberInfo });
  const [hasEmergencyContact, setHasEmergencyContact] = useState(false);
  
  // Fetch member stats on mount
  useEffect(() => {
    if (memberInfo?.id) {
      fetchMemberStats();
      fetchInsurance();
    }
  }, [memberInfo?.id, fetchMemberStats, fetchInsurance]);
  
  // Fetch emergency contact on mount
  useEffect(() => {
    const fetchEmergencyContact = async () => {
      if (memberInfo?.id) {
        try {
          const data = await emergencyInfoService.getEmergencyInfo(memberInfo.id);
          // Check if emergency contact exists and has required fields
          setHasEmergencyContact(
            !!(data?.family_contact?.name && data?.family_contact?.phone)
          );
          // Refetch member stats after emergency info is loaded
          // This ensures emergencyCardReady flag is updated based on backend check
          await fetchMemberStats();
        } catch (error) {
          setHasEmergencyContact(false);
        }
      }
    };
    fetchEmergencyContact();
  }, [memberInfo?.id, fetchMemberStats]);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
      screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_CREATE_STEP_1,
    });
  }, []);

  // Refetch data when component becomes active (navigating back from vitals/insurance/contacts)
  useEffect(() => {
    const refetchData = async () => {
      if (memberInfo?.id) {
        triggerRefetch();
        await fetchInsurance();
        await fetchMemberStats();
        
        // Refetch emergency contact
        try {
          const data = await emergencyInfoService.getEmergencyInfo(memberInfo.id);
          setHasEmergencyContact(
            !!(data?.family_contact?.name && data?.family_contact?.phone)
          );
        } catch (error) {
          setHasEmergencyContact(false);
        }
      }
    };

    // Refetch on mount and when dependencies change
    refetchData();
  }, [memberInfo?.id, triggerRefetch, fetchInsurance, fetchMemberStats]);

  // Check if member is a child
  const isChild = isChildUtil({
    dobYear: memberInfo.dobYear,
    dobMonth: memberInfo.dobMonth,
    dobDay: memberInfo.dobDay,
  });

  // Check if member is a pet
  const isPet = memberInfo.memberType === 'p';

  // Emergency card specific sections in the exact order from screenshot
  // For children, include birthComplications after bloodType
  // For pets, include petCare after allergies
  const orderedSectionIds = [
    'bloodType',
    ...(isChild ? ['birthComplications'] : []),
    'vitals',
    'allergies',
    ...(isPet ? ['petCare'] : []),
    'conditions',
    'medicine',
    'surgeries',
    'deviceImplants',
    'insurance',
    'note',
    'contacts',
  ];

  // Create a map of all available sections
  let allSections = [
    ...healthProfileNav,
    userProfileNav.find(item => item.id === 'vitals'),
    userProfileNav.find(item => item.id === 'insurance'),
    userProfileNav.find(item => item.id === 'contacts'),
  ].filter(Boolean);

  // For pets, remove birthComplications if it exists
  if (isPet) {
    allSections = allSections.filter(item => item.id !== 'birthComplications');
  }

  // For children (but not pets), manually add birthComplications if not already present
  // This is needed because healthProfileNav is filtered by derivedMemberType, not age
  if (isChild && !isPet && !allSections.find(item => item.id === 'birthComplications')) {
    const birthComplicationsSection = {
      id: 'birthComplications',
      label: t('healthProfile.nav.birthInformation'),
      labelKey: 'healthProfile.nav.birthInformation',
      path: '/profile/health-profile/birth-complications',
      derivedTypes: ['C'],
      icon: null,
    };
    allSections.push(birthComplicationsSection as any);
  }

  // For pets, manually add petCare if not already present
  if (isPet && !allSections.find(item => item.id === 'petCare')) {
    const petCareSection = {
      id: 'petCare',
      label: t('healthProfile.nav.petCare'),
      labelKey: 'healthProfile.nav.petCare',
      path: '/profile/health-profile/pet-care',
      derivedTypes: ['P'],
      icon: null,
    };
    allSections.push(petCareSection as any);
  }

  const sectionMap = new Map(allSections.map(section => [section.id, section]));

  // Order sections according to the specified order
  const emergencyCardSections = orderedSectionIds
    .map(id => sectionMap.get(id))
    .filter(Boolean);

  const getType = (id: string) => {
    const values = Object.values(MedicalDetailType);
    return values.find((value) => value === id);
  };

  // Helper function to get display value from summary
  const getDisplayFromSummary = (type: MedicalDetailType): { message: string, isEmpty: boolean } => {
    if (!summary?.summary) return { message: t('common.notAnswered'), isEmpty: true };

    const detail = summary.summary[type];
    if (!detail) return { message: type === MedicalDetailType.NOTE ? t('healthProfile.placeholders.noNote') : t('common.notAnswered'), isEmpty: true };

    if (detail.status === MedicalDetailStatus.NOT_ANSWERED) {
      return { message: t('common.notAnswered'), isEmpty: true };
    } else if (detail.status === MedicalDetailStatus.NONE) {
      return { message: t('healthProfile.placeholders.none'), isEmpty: false };
    } else if (detail.status === MedicalDetailStatus.ANSWERED) {
      if (detail.count && typeof detail.count === 'number') {
        return { message: t(
          `healthProfile.${type}.count.${
            detail.count === 1 ? 'singular' : 'plural'
          }`,
          { count: detail.count }
        ), isEmpty: false };
      } else if (detail.value) {
        if (type === MedicalDetailType.BLOOD_TYPE) {
          return { message: t(`healthProfile.bloodType.${detail.value}`), isEmpty: false };
        }
        return { message: t(`healthProfile.${type}.value`, { value: detail.value }), isEmpty: false };
      } else {
        return { message: t(`healthProfile.${type}.answered`), isEmpty: false };
      }
    }
    return { message: t('common.notAnswered'), isEmpty: true };
  };

  const handleNone = async (type: MedicalDetailType) => {
    try {
      await updateNone(type);
      setShowDialog(false);
      setPendingNav(null);
    } catch (error) {
      showToast({
        message: 'Failed to update preference. Please try again.',
        variant: 'error',
      });
    }
  };

  const handleItemClick = (item: any) => {
    // For non-health profile sections (vitals, insurance, contacts), navigate directly
    if (['vitals', 'insurance', 'contacts'].includes(item.id)) {
      // For contacts, add query parameter to open emergency contact accordion
      const path = item.id === 'contacts' ? `${item.path}?open=emergency` : item.path;
      navigate(path, {
        state: { backUrl: window.location.pathname },
      });
      return;
    }

    const type = getType(item.id);
    if (
      [
        MedicalDetailType.BLOOD_TYPE,
        MedicalDetailType.VACCINE_TRACKER,
        MedicalDetailType.PET_CARE,
      ].includes(type)
    ) {
      navigate(item.path, {
        state: { backUrl: window.location.pathname },
      });
      return;
    }

    const detail = summary.summary[type];
    // Determine if the section has data using summary
    if ([MedicalDetailStatus.ANSWERED].includes(detail?.status)) {
      navigate(item.path, {
        state: { backUrl: window.location.pathname },
      });
    } else if ([MedicalDetailStatus.NONE].includes(detail?.status)) {
      navigate(item.defaultState || item.path, {
        state: { backUrl: window.location.pathname },
      });
    } else {
      // Use defaultState if present
      const targetPath = item.defaultState || item.path;
      setPendingNav({ path: targetPath, id: item.id, type });
      setShowDialog(true);
      
      // Track popup for long-term conditions
      if (item.id === 'conditions') {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.LONG_TERM_CONDITION_POPUP,
        });
      }
      
      // Track popup for medicine
      if (item.id === 'medicine') {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.MEDICINE_POPUP,
        });
      }
      
      // Track popup for surgeries
      if (item.id === 'surgeries') {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_POPUP,
        });
      }
      
      // Track popup for device implants
      if (item.id === 'deviceImplants') {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.IMPLANT_POPUP,
        });
      }
      
      // Track popup for emergency note
      if (item.id === 'note') {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.EMERGENCY_CARD,
          screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_NOTE_POPUP,
        });
      }
    }
  };

  // Calculate progress based on real data
  const completedCount = emergencyCardSections.filter(item => {
    const type = getType(item.id);
    // For non-health profile items (vitals, insurance, contacts), check if they exist
    if (['vitals', 'insurance', 'contacts'].includes(item.id)) {
      if (item.id === 'vitals') {
        const vitalsArray = vitals && typeof vitals === 'object' && 'vitals' in vitals && Array.isArray((vitals as any).vitals)
          ? (vitals as any).vitals
          : [];
        return vitalsArray.length > 0;
      } else if (item.id === 'insurance') {
        return insurances && insurances.length > 0;
      } else if (item.id === 'contacts') {
        return hasEmergencyContact;
      }
      return false;
    }
    const { isEmpty } = getDisplayFromSummary(type);
    return !isEmpty;
  }).length;
  const totalCount = emergencyCardSections.length;
  const progress = Math.round((completedCount / totalCount) * 100);
  
  // Use emergencyCardReady from member stats or member object to determine if card is ready
  // Check both sources to ensure we catch the flag from either the stats or the member object
  const isCardReady = memberStats?.emergencyCardReady ?? memberInfo.emergencyCardReady ?? false;
  
  // Check if all sections are completed (100% progress)
  const isAllSectionsComplete = progress === 100;
  
  // Backup: Mark emergency card complete when all sections are done
  useEffect(() => {
    const markCardComplete = async () => {
      if (isAllSectionsComplete && memberInfo?.current_family && memberInfo?.id) {
        try {
          // Backup call to ensure emergencyCardComplete flag is set
          await familyStatsService.markEmergencyCardComplete(memberInfo.id);
          // Refetch member stats to update UI and show complete toast
          await fetchMemberStats();
          // Show the complete toast after successful update
          setShowCompleteToast(true);
        } catch (error) {
          console.error('Failed to mark emergency card complete:', error);
        }
      }
    };
    markCardComplete();
  }, [isAllSectionsComplete, memberInfo?.current_family, memberInfo?.id, fetchMemberStats]);

  // Show success toast when card is ready
  const [showUnlockedToast, setShowUnlockedToast] = useState(true);
  const [showCompleteToast, setShowCompleteToast] = useState(true);
  
  // Track initial emergency card ready state to detect first unlock
  const initialCardReadyState = React.useRef<boolean | null>(null);
  const hasTrackedFirstUnlock = React.useRef(false);
  
  // Store initial card ready state on first render
  useEffect(() => {
    if (initialCardReadyState.current === null) {
      const initialState = memberStats?.emergencyCardReady ?? memberInfo.emergencyCardReady ?? false;
      initialCardReadyState.current = initialState;
    }
  }, [memberStats?.emergencyCardReady, memberInfo.emergencyCardReady]);
  
  // Track when card becomes unlocked (emergencyCardReady changes from false to true)
  useEffect(() => {
    if (initialCardReadyState.current !== null && !hasTrackedFirstUnlock.current) {
      const wasNotReady = initialCardReadyState.current === false;
      const isNowReady = isCardReady === true;
      
      if (wasNotReady && isNowReady) {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
          screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CARD_CREATE_STEP_1,
          action: 'first_emergency_card_completed',
        });
        hasTrackedFirstUnlock.current = true;
      }
    }
  }, [isCardReady, memberInfo]);

  const handleViewCard = () => {
    navigate('/dashboard/emergency-card', {
      state: { backUrl: window.location.pathname },
    });
  };

  if (loading || !summary) {
    return (
      <div>
        <MLoader size={40} />
      </div>
    );
  }

  return (
    <div style={{ padding: '0 24px' }}>
      <MHeader
        title="Emergency card"
        module="emergency-card"
        memberInfo={memberInfo}
        showGradient
        showProgress
        progress={progress}
        completedCount={completedCount}
        totalCount={totalCount}
      />

      <MSpacer mt={24} />
      
      <div>
        {/* Show unlocked toast when emergencyCardReady is true */}
        {isCardReady && showUnlockedToast && !isAllSectionsComplete ? (
          <div className={styles.successToast}>
            <div className={styles.toastIconWrapper}>
              <IconLockOpen size={20} className={styles.toastIcon} />
            </div>
            <div className={styles.toastContent}>
              <div className={styles.toastTitle}>Emergency card unlocked</div>
              <div className={styles.toastDescription}>
                Complete remaining sections to reach 100% and be care ready.
              </div>
              <button
                className={styles.toastCta}
                onClick={handleViewCard}
              >
                View card
              </button>
            </div>
            <button className={styles.toastClose} onClick={() => setShowUnlockedToast(false)}>
              <IconX size={20} />
            </button>
          </div>
        ) : isAllSectionsComplete && showCompleteToast ? (
          /* Show complete toast when all sections are done (100% progress) */
          <div className={styles.completeToast}>
            <div className={styles.toastIconWrapper}>
              <IconMedicalCross size={20} className={styles.completeToastIcon} />
            </div>
            <div className={styles.toastContent}>
              <div className={styles.completeToastTitle}>Your emergency card is complete</div>
              <div className={styles.completeToastDescription}>
                You can update it anytime if your health information changes.
              </div>
              <button
                className={styles.completeToastCta}
                onClick={handleViewCard}
              >
                View card
              </button>
            </div>
            <button className={styles.toastClose} onClick={() => setShowCompleteToast(false)}>
              <IconX size={20} className={styles.completeToastCloseIcon} />
            </button>
          </div>
        ) : (
          /* Show description card when sections are incomplete */
          <div className={styles.descriptionCard}>
            <p className={styles.descriptionText}>
              Add vitals, emergency contacts, blood group, allergies, and medicines to unlock the card.
            </p>
            <p className={styles.descriptionText}>
              To strengthen your card, add details as long-term conditions, health insurance and any past surgeries.
            </p>
          </div>
        )}
        
        <MSpacer mt={24} />

        {/* Sections List */}
        <div className={styles.sectionsContainer}>
          <MSpacer mt={16} />
          
          <div className="profile-list">
            {emergencyCardSections.map((item) => {
              // Get the translated label - use special label for emergency contacts
              const label = item.id === 'contacts' ? 'Emergency contacts' : t(item.labelKey);

              // For non-health profile sections, show "Not Answered"
              let message = '';
              let isEmpty = true;
              let isCompleted = false;

              if (['vitals', 'insurance', 'contacts'].includes(item.id)) {
                // Check completion status for vitals, insurance, and contacts
                if (item.id === 'vitals') {
                  const vitalsArray = vitals && typeof vitals === 'object' && 'vitals' in vitals && Array.isArray((vitals as any).vitals)
                    ? (vitals as any).vitals
                    : [];
                  const hasVitals = vitalsArray.length > 0;
                  if (hasVitals) {
                    message = "Added";
                    isEmpty = false;
                    isCompleted = true;
                  } else {
                    message = t('common.notAnswered');
                    isEmpty = true;
                    isCompleted = false;
                  }
                } else if (item.id === 'insurance') {
                  const hasInsurance = insurances && insurances.length > 0;
                  if (hasInsurance) {
                    const count = insurances.length;
                    message = `${count} insurance polic${count === 1 ? 'y' : 'ies'}`;
                    isEmpty = false;
                    isCompleted = true;
                  } else {
                    message = t('common.notAnswered');
                    isEmpty = true;
                    isCompleted = false;
                  }
                } else if (item.id === 'contacts') {
                  if (hasEmergencyContact) {
                    message = 'Added';
                    isEmpty = false;
                    isCompleted = true;
                  } else {
                    message = t('common.notAnswered');
                    isEmpty = true;
                    isCompleted = false;
                  }
                }
              } else {
                const type = getType(item.id);
                // Custom logic for each field using summary data
                const displayData = getDisplayFromSummary(type);
                message = displayData.message;
                isEmpty = displayData.isEmpty;
                isCompleted = !isEmpty;
              }

              return (
                <div 
                  key={item.id} 
                  className={styles.sectionItem}
                  onClick={() => handleItemClick(item)}
                >
                  <div className={styles.sectionCheckbox}>
                    {isCompleted ? (
                      <div className={styles.checkboxCompleted}>
                        <IconCheck size={14} strokeWidth={2.5} />
                      </div>
                    ) : (
                      <svg 
                        className={styles.checkboxEmpty}
                        width="20" 
                        height="20" 
                        viewBox="0 0 20 20" 
                        fill="none"
                      >
                        <circle 
                          cx="10" 
                          cy="10" 
                          r="9" 
                          stroke="currentColor" 
                          strokeWidth="1.6"
                        />
                      </svg>
                    )}
                  </div>
                  <div className={styles.sectionContent}>
                    <div className={styles.sectionLabel}>{label}</div>
                    <div className={isEmpty ? styles.sectionInfoEmpty : styles.sectionInfo}>
                      {message}
                    </div>
                  </div>
                  <div className={styles.sectionChevron}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {pendingNav && (
          <MDialog
            isOpen={showDialog}
            onClose={() => {
              setShowDialog(false);
              setPendingNav(null);
            }}
            title={t(`healthProfile.${pendingNav.type}.none.title${memberInfo?.onBehalf ? '_on_behalf' : ''}`)}
            description={t(`healthProfile.${pendingNav.type}.none.description`)}
            primaryCtaText={t('common.yes')}
            onPrimaryCta={() => {
              setShowDialog(false);
              
              // Track when Yes is clicked for conditions
              if (pendingNav.id === 'conditions') {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.LONG_TERM_CONDITION_ADD,
                });
              }
              
              // Track when Yes is clicked for medicine
              if (pendingNav.id === 'medicine') {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.MEDICINE_ADD,
                });
              }
              
              // Track when Yes is clicked for surgeries
              if (pendingNav.id === 'surgeries') {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.PROCEDURE_ADD,
                });
              }
              
              // Track when Yes is clicked for device implants
              if (pendingNav.id === 'deviceImplants') {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.IMPLANT_ADD,
                });
              }
              
              // Track when Yes is clicked for emergency note
              if (pendingNav.id === 'note') {
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_NOTE_ADD,
                });
              }
              
              navigate(pendingNav.path, {
                state: { backUrl: window.location.pathname },
              });
              setPendingNav(null);
            }}
            secondaryCtaText={t('common.no')}
            onSecondaryCta={() => {
              handleNone(pendingNav.type);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ActivateEmergencyCard;
